import {parseISO, startOfToday} from 'date-fns';
import {
  Button,
  ButtonGroup,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  closeCurrentDialog,
} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';
import {object} from 'yup';

import {head} from 'ramda';

import {DeputyPersonV2, PersonalIdType} from '@dms/api/base';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import {yupDate, yupString} from 'shared/web';

import {useGetPersonalIdTypes} from '../hooks/useGetPersonalIdTypes';

type FormValues = {
  isPrimary: boolean;
  firstName: string;
  lastName: string | null;
  dateOfBirth: Date | null;
  identificationNumber: string | null;
  personalIdType: PersonalIdType | null;
  personalIdNumber: string | null;
  validUntil: Date | null;
  issuedBy: string | null;
};

interface DeputyPersonFormProps {
  handleSubmit: (values: DeputyPersonV2) => void;
  defaultValues?: DeputyPersonV2;
}

export function DeputyPersonForm(props: DeputyPersonFormProps) {
  const {types} = useGetPersonalIdTypes();

  const handleSubmit: FormSubmitHandler<FormValues> = async (values) => {
    const submitValues: DeputyPersonV2 = {
      ...values,
      isPrimary: false,
      dateOfBirth: values.dateOfBirth?.toISOString() ?? null,
      validUntil: values.validUntil?.toISOString() ?? null,
    };

    await props.handleSubmit(submitValues);
    closeCurrentDialog();
  };

  const defaultValues: Partial<FormValues> = {
    ...props.defaultValues,
    dateOfBirth: props.defaultValues?.dateOfBirth
      ? parseISO(props.defaultValues.dateOfBirth)
      : null,
    validUntil: props.defaultValues?.validUntil ? parseISO(props.defaultValues.validUntil) : null,
    personalIdType: props.defaultValues?.personalIdType ?? head(types)?.value,
  };

  return (
    <Form<FormValues>
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      schema={schema}
      data-testid={testIds.settings.billingInformation('deputyPersonForm')}
    >
      {(control) => (
        <VStack spacing={5}>
          <Grid columns={2}>
            <FormField
              type="text"
              control={control}
              name="firstName"
              label={i18n.t('entity.person.labels.firstName')}
              isRequired
              data-testid={testIds.settings.billingInformation('firstName')}
            />
            <FormField
              type="text"
              control={control}
              name="lastName"
              label={i18n.t('entity.person.labels.lastName')}
              data-testid={testIds.settings.billingInformation('lastName')}
              isRequired
            />
            <FormField
              type="date"
              control={control}
              name="dateOfBirth"
              maxDate={startOfToday()}
              label={i18n.t('entity.person.labels.birthdate')}
              data-testid={testIds.settings.billingInformation('birthDate')}
            />
            <FormField
              type="text"
              control={control}
              name="identificationNumber"
              label={i18n.t('entity.customer.labels.registrationNumber')}
              data-testid={testIds.settings.billingInformation('registrationNumber')}
            />
            <FormField
              type="choice"
              control={control}
              isNotClearable
              name="personalIdType"
              options={types}
              label={i18n.t('entity.person.labels.personalIdType')}
              data-testid={testIds.settings.billingInformation('personalIdType')}
            />
            <FormField
              type="text"
              control={control}
              name="personalIdNumber"
              label={i18n.t('entity.person.labels.idNumber')}
              data-testid={testIds.settings.billingInformation('idNumber')}
            />
            <FormField
              type="date"
              control={control}
              name="validUntil"
              label={i18n.t('general.labels.validUntil')}
              minDate={startOfToday()}
              data-testid={testIds.settings.billingInformation('validUntil')}
            />
            <FormField
              type="text"
              control={control}
              name="issuedBy"
              label={i18n.t('general.labels.issuedBy')}
              data-testid={testIds.settings.billingInformation('issuedBy')}
            />
          </Grid>

          <ButtonGroup align="right">
            <Button
              title={i18n.t('general.actions.discard')}
              onClick={closeCurrentDialog}
              variant="secondary"
              data-testid={testIds.settings.billingInformation('discard')}
            />
            <FormButton
              control={control}
              type="submit"
              data-testid={testIds.settings.billingInformation('saveForm')}
              title={i18n.t('general.actions.save')}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}

const schema = object({
  firstName: yupString.required(),
  lastName: yupString.required(),
  dateOfBirth: yupDate.default(null),
  identificationNumber: yupString.default(null),
  personalIdType: yupString.default(null),
  personalIdNumber: yupString.default(null),
  validUntil: yupDate.default(null),
  issuedBy: yupString.default(null),
});
